import { Injectable } from '@angular/core';

import { Observable } from 'rxjs';
import { filter, map, take } from 'rxjs/operators';

import { HttpVerbsEnum } from '../shared/enums/http-verbs.enum';
import { TargetingListListApi } from './model/targeting-list-list-api.model';
import { TargetingQueryApi } from './model/targeting-search.api.model';
import { TargetingSearchResponseApiModel } from './model/targeting-search-response.api.model';
import { PortfolioApiModel, TargetingListApi } from './model/targeting-list-api.model';
import { EntityIdApiModel } from './model/entity-id-api-model';
import { SortOrderEnum } from '../shared/enums/sort-order.enum';
import { ListMembershipApiModel } from './model/list-membership-api-model';
import { NeptuneApi } from '../genericApi/neptune.api';
import { CurrentUserStateService } from '../../Core/Users/services/current-user-state.service';
import { TargetingListTypeEnum } from '../../Discover/Targeting/enums/targeting-list-type.enum';
import { DisplayGroupPatchModel } from '../displayGroups/models/display-group-patch.api.model';
import { TargetingListContactEmployment } from './model/targeting-list-contact-employment-api.model';
import { MultiBaseAbstractApi } from '../genericApi/multi-base-abstract.api';

/** These are the endpoints that when called should reload targeting lists in other tabs through web socket */
export const wsReloadListRequests: RegExp[] = [
    /\/targetinglists\/$/,
    /\/targetinglists\/displaygroups\//,
    /\/targetinglists\/[0-9]+$/,
    /\/targetinglists\/addentities/,
    /\/targetinglists\/addsearchresults/,
    /\/targetinglists\/[0-9]+\/removeentities/,
    /\/targetinglists\/[0-9]+\/removesearchresults/,
    /\/distributions\/report\/[0-9]+\/targetinglists\/addrecipients/,
    /\/distributions\/report\/[0-9]+\/targetinglists\/removerecipients/,
    /\/outlets\/public\/[a-z0-9\-]\/addallstafftolists/
];

@Injectable()
export class TargetingListsApiService extends MultiBaseAbstractApi<TargetingListApi, TargetingListListApi> {

    private locale: string = 'en-US';

    constructor(
        private neptuneApi: NeptuneApi,
        private currentUserStateService: CurrentUserStateService,
    ) {
        super();

        this.currentUserStateService.getDataReferenceLanguageTag().pipe(
            filter(x => !!x),
            take(1)
        ).subscribe(languageTag => {
            this.locale = languageTag;
        });
    }

    getList(): Observable<TargetingListListApi[]> {
        const slug: string = '/targetinglists/';

        return this.neptuneApi.api<TargetingListListApi[]>(
            slug,
            null,
            HttpVerbsEnum.GET
        );
    }

    create(list: TargetingListApi): Observable<number> {
        const slug: string = '/targetinglists/';

        return this.neptuneApi.api<number>(
            slug,
            list,
            HttpVerbsEnum.POST

        );
    }

    /** @TODO - change endpoint to return id */
    update(list: TargetingListApi): Observable<number> {
        const slug: string = `/targetinglists/${list.id}`;

        return this.neptuneApi.api<void>(
            slug,
            list,
            HttpVerbsEnum.PUT
        ).pipe(
            map(() => list.id) // @HACK
        );
    }

    /** @TODO - change endpoint to return id */
    deleteById(id: number): Observable<number> {
        const slug: string = `/targetinglists/${id}`;

        return this.neptuneApi.api<void>(
            slug,
            null,
            HttpVerbsEnum.DELETE
        ).pipe(
            map(() => id)
        );
    }

    getById(id: number, getCount: boolean = false): Observable<TargetingListApi> {
        const slug: string = `/targetinglists/${id}?getCount=${getCount}`;

        return this.neptuneApi.api<TargetingListApi>(
            slug,
            null,
            HttpVerbsEnum.GET
        );
    }

    addSearchResultsToList(ids: number[], targetingQuery: TargetingQueryApi, listType?: TargetingListTypeEnum): Observable<void> {
        let slug: string = `/targetinglists/addsearchresults?locale=${this.locale}`;
        if (listType) {
            slug += `&listType=${listType}`;
        }

        return this.neptuneApi.api<void>(
            slug,
            {
                listIds: ids,
                query: targetingQuery
            },
            HttpVerbsEnum.POST
        );
    }

    addEntitiesToList(ids: number[], entityIds: EntityIdApiModel[]): Observable<void> {
        const slug: string = '/targetinglists/addentities';

        return this.neptuneApi.api<void>(
            slug,
            {
                listIds: ids,
                entityIds: entityIds
            },
            HttpVerbsEnum.POST
        );
    }

    removeEntitiesFromList(id: number, entityIds: EntityIdApiModel[]): Observable<void> {
        const slug: string = `/targetinglists/${id}/removeentities`;

        return this.neptuneApi.api<void>(
            slug,
            entityIds,
            HttpVerbsEnum.POST
        );
    }

    // remove multiple entities from multiple lists
    public removeEntitiesFromLists(entities: EntityIdApiModel[], listIds: number[]): Observable<void> {
        const slug: string = '/targetinglists/removeentities';
        return this.neptuneApi.api<void>(
            slug,
            {
                entities,
                listIds
            },
            HttpVerbsEnum.POST
        );
    }

    public removeEntitiesMatchingSearch(id: number, query: TargetingQueryApi): Observable<void> {
        const slug: string = `/targetinglists/${id}/removesearchresults?locale=${this.locale}`;

        return this.neptuneApi.api<void>(
            slug,
            query,
            HttpVerbsEnum.POST
        );
    }

    getListMemberships(entityIds: EntityIdApiModel[]): Observable<ListMembershipApiModel[]> {
        const slug: string = '/targetinglists/listmembership';

        return this.neptuneApi.api<ListMembershipApiModel[]>(
            slug,
            entityIds,
            HttpVerbsEnum.POST
        );
    }


    createListForDistributionIndividualRecipients(): Observable<number> {
        const slug = '/targetinglists/distributionindividualrecipients';

        return this.neptuneApi.api<number>(
            slug,
            null,
            HttpVerbsEnum.POST
        );
    }

    getPortfolios(): Observable<PortfolioApiModel[]> {
        const slug = '/targetinglists/portfolios';

        return this.neptuneApi.api<PortfolioApiModel[]>(
            slug,
            null,
            HttpVerbsEnum.GET
        );
    }

    /**
     * Adds and/or removes targeting lists from provided group
     * @param addIds Lists to add to group
     * @param removeIds Lists to remove from group
     * @param groupId
     * @returns
     */
    updateListDisplayGroup(addIds: number[], removeIds: number[], groupId: number): Observable<void> {
        const slug: string = `/targetinglists/displaygroups/${groupId}`;

        const patchModel: DisplayGroupPatchModel = {
            addResourceIds: addIds,
            removeResourceIds: removeIds
        };

        return this.neptuneApi.api<void>(
            slug,
            patchModel,
            HttpVerbsEnum.PATCH
        );
    }

    /**
     * Removes group ID from all provided targeting lists
     * @param removeIds Lists to remove from groups
     * @returns
     */
    removeListsFromDisplayGroup(removeIds: number[]): Observable<void> {
        const slug: string = '/targetinglists/displaygroups';

        const patchModel: DisplayGroupPatchModel = {
            addResourceIds: [],
            removeResourceIds: removeIds
        };

        return this.neptuneApi.api<void>(
            slug,
            patchModel,
            HttpVerbsEnum.PATCH
        );
    }

    /**
     * Adds and/or removes targeting lists from favourites
     * @param addIds Lists to add to favourites
     * @param removeIds Lists to remove from favourites
     * @returns
     */
    updateFavorites(addIds: number[], removeIds: number[]): Observable<void> {
        const slug: string = '/targetinglists/displaygroups/favorites';

        const patchModel: DisplayGroupPatchModel = {
            addResourceIds: addIds,
            removeResourceIds: removeIds
        };

        return this.neptuneApi.api<void>(
            slug,
            patchModel,
            HttpVerbsEnum.PATCH
        );
    }

    updateListContactEmployment(listId: number, contactUuid: string, employmentId: number): Observable<void>  {
        const slug = `/targetinglists/${listId}/contacts/${contactUuid}/employment`;

        const payload: TargetingListContactEmployment = {
            employmentId: employmentId
        };

        return this.neptuneApi.api<void>(
            slug,
            payload,
            HttpVerbsEnum.PUT
        );
    }
}
